/**
 * Business Profile read-model contract, v1 (XC-ERP1-U3 §5.2; plan §6.3 C-12).
 *
 * `GET /api/v1/business-profile` (gateway → entity_resolution `/business-profile`)
 * returns `BusinessProfileReadModel`. `readModel.v1.example.json` beside this file
 * is the worked example; the ER route (C-13) emits this shape.
 *
 * The schema refuses any fact the page could not show honestly (XC-BP1 §12
 * wrong-data tests). A payload that fails is refused whole; the page paints none
 * of it. The rules:
 * - every displayed value has an evidence row or a confirmer (U3 §5.2), except a
 *   `default`, which is Wingo's preset and is shown as "Default"; a confirmed or
 *   suggested chip is never painted over no value;
 * - a fact whose latest verify failed is `needs_attention` and nothing else
 *   (XC-BP1 §8). This holds only for a verify the ledger persisted: today the
 *   ledger persists none (U3 RED, class 8a2749a0), so `verify` is null and the
 *   page says "not yet checked against your data" rather than painting an
 *   unchecked fact like a checked one;
 * - v1 shows no date for a customer's answer (advisor ruling 2026-10-03 06:15):
 *   the ledger stamps `answered_at` with its own write time when the source
 *   gives none, so a session filing an emailed answer later records the filing
 *   day (LOWES: Sep 15 for an answer emailed Sep 2). `confirmed_by.at` is null,
 *   never a date, and History lists only the customer's own answers
 *   (`state: 'answered'`, no date, no other key); an engine state (executed,
 *   verified, superseded, withdrawn) is refused, since a ledger "verified" is
 *   not backed by a persisted verify (U3 RED 8a2749a0). Dates and engine
 *   history return when U3 records the answer time and persists verify;
 * - every string the page shows (`value_display`, an evidence row's `label`, a
 *   confirmer's `display`, a history entry's `by`, `held_reason`, `attention`)
 *   is customer copy (`isDisplayText`): never a UUID, a snake_case code in any
 *   case, a `|`-delimited or long hex id, or a dotted/slashed code path, and
 *   never the fact's own ruling ref or an evidence row id. Codes stay in the
 *   fields the page never prints (`kind`, `subtype` humanized, `value`, `table`,
 *   `id`, `source`). `__fixtures__/displayText.cases.json` holds the cases this
 *   schema and the ER route's mirror (`business_profile_text.py`) both run;
 * - `unfiled` lists earlier answers no fact group takes yet; a ruling ref
 *   appears once across the groups and `unfiled`;
 * - whitespace is JavaScript's (`\s`, `trim`), and the ER route's mirror reads
 *   the same set character for character (U+FEFF is whitespace; U+0085 and
 *   U+001C-U+001F are not): `__fixtures__/displayText.parity.json` holds the
 *   route's verdict on 1,500 seeded strings, and `contractParity.test.ts`
 *   requires this schema's to match (/ship round 10). The route holds each fact
 *   to these rules on its own and withholds one that fails (`meta.facts_withheld`).
 *
 * `__fixtures__/routePayload.v1.json` is the ER route's own output (C-13
 * regenerates and pins it); `routePayload.test.ts` parses it with this schema.
 */

import { z } from 'zod';

/** The seven statuses a fact can show, in display order. */
export const FACT_STATUSES = [
  'confirmed',
  'suggested',
  'default',
  'conflict',
  'held',
  'needs_attention',
  'unknown',
] as const;

/** XC-BP1 §4: the seven facts, in page order. */
export const FACT_GROUPS = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7'] as const;

const isoDateTime = z.string().datetime({ offset: true });
const nonBlank = z.string().refine((s) => s.trim().length > 0, 'must not be blank');
const count = z.number().int().nonnegative();

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
/** A snake_case code or table name, in any case (`no_dual_role_partners`, `NO_DUAL_ROLE`, `stg_qb__vendors`). */
const CODE_WORD_RE = /^[a-z0-9]+(?:_+[a-z0-9]+)+$/i;
/** A hex id of 12+ characters with a digit in it (a Gmail id such as `18c2f0a9b7d41e55`). */
const HEX_ID_RE = /^(?=[0-9a-f]*[0-9])[0-9a-f]{12,}$/i;
/** An id shorter than this is not looked for inside display text (`R1` would match too much). */
const MIN_ID_LENGTH = 4;

const isCode = (part: string): boolean => CODE_WORD_RE.test(part) || HEX_ID_RE.test(part);

/** `\w` without the `u` flag: ASCII letters, digits and `_` (by UTF-16 code unit). */
const isWordUnit = (c: number): boolean =>
  (c >= 48 && c <= 57) || (c >= 65 && c <= 90) || (c >= 97 && c <= 122) || c === 95;

/**
 * `word` without its leading and trailing non-word characters, as `/^\W+|\W+$/g` removes. Two
 * pointers, so linear in the word's length: the regex form backtracks quadratically on a long
 * non-word run inside a word. The ER route's twin is `business_profile_text.strip_edges`.
 */
export const stripEdges = (word: string): string => {
  let start = 0;
  let end = word.length;
  while (start < end && !isWordUnit(word.charCodeAt(start))) start += 1;
  while (end > start && !isWordUnit(word.charCodeAt(end - 1))) end -= 1;
  return word.slice(start, end);
};

const isCodeOrId = (word: string): boolean => {
  const bare = stripEdges(word);
  if (!bare) return false;
  if (isCode(bare) || bare.includes('|')) return true;
  // A dotted, slashed or colon path of codes (`self_identity.legal_name`), but an
  // address keeps its dots (`ap_team.billing@acme.example` is a value).
  return !bare.includes('@') && bare.split(/[./:]/).some(isCode);
};

/**
 * Customer copy: not blank, no UUID, and no word that is a code, a table name, a
 * `|`-delimited id, a long hex id or a code path. The ER route mirrors this rule
 * (`business_profile_text.is_display_text`); both run `displayText.cases.json`.
 */
export const isDisplayText = (s: string): boolean =>
  s.trim().length > 0 && !UUID_RE.test(s) && !s.split(/\s+/).some(isCodeOrId);

const displayText = z
  .string()
  .refine(isDisplayText, 'display text is never blank, a code, a table name or an id');

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(JsonValueSchema),
  ]),
);

export const FactStatusSchema = z.enum(FACT_STATUSES);
export const FactGroupSchema = z.enum(FACT_GROUPS);

/**
 * One row a value is read from: never prose only (U3 §2 `evidence`). `table` and
 * `id` name a real row; `label` is what the page shows for it, naming the row in
 * the customer's terms (e.g. "QuickBooks vendor Sysco Foods").
 */
export const EvidenceRowSchema = z.object({
  table: nonBlank,
  id: nonBlank,
  value: JsonValueSchema,
  label: displayText,
});

export const ConfirmedBySchema = z.object({
  user_id: z.string().nullable(),
  display: displayText,
  /** Always null in v1: the ledger's `answered_at` may be the day a session filed the answer. */
  at: z.null(),
  /** The ledger `source` of the confirming row, e.g. `gmail`, `in_app_profile`. */
  source: nonBlank,
});

export const VerifySchema = z.object({
  passed: z.boolean(),
  at: isoDateTime,
});

/** Read from rows after the executor drains, never typed (XC-BP1 §3.8). */
export const PayoffSchema = z.object({
  rows_closed: count,
  question_keys_closed: count,
  measured_at: isoDateTime,
});

/** The customer's own answer, in plain words: no date, and no engine state (v1). */
export const HistoryEntrySchema = z
  .object({
    state: z.literal('answered'),
    /** Who answered, as the page shows it: a person's name. Never a user id. */
    by: displayText,
  })
  .strict();

type ShownFact = {
  ruling_ref: string;
  value_display: string | null;
  held_reason: string | null;
  attention: string | null;
  confirmed_by: { display: string } | null;
  evidence: { id: string; label: string }[];
  history: { by: string }[];
  /** A one-tap suggestion's source note (data: held to the same rules). */
  source_note?: string | null;
};
/** Every string of a fact the page prints. */
const shownStrings = (f: ShownFact): string[] =>
  [
    f.value_display,
    f.held_reason,
    f.attention,
    f.confirmed_by?.display ?? null,
    ...f.evidence.map((e) => e.label),
    ...f.history.map((h) => h.by),
    f.source_note ?? null,
  ].filter((s): s is string => s !== null);
/** The ids a fact carries, which none of its shown strings may contain. */
const factIds = (f: ShownFact): string[] => [f.ruling_ref, ...f.evidence.map((e) => e.id)];
/** `text` contains one of `ids` (each at least MIN_ID_LENGTH UTF-16 code units long). */
export const namesAnId = (text: string, ids: string[]): boolean =>
  ids.some((id) => id.length >= MIN_ID_LENGTH && text.includes(id));

/**
 * XC-BP1-W2 §4 (ERP1-P2-BP-CONFIRM-ROUTE, `FactAction`): one button on a Suggested row, enabled or
 * disabled with the one plain reason why; a one-tap button (`confirm`, `not_ours`) also carries the
 * `value` its tap files, sent back verbatim (absent where the button is not a one tap).
 */
export const FactActionSchema = z.object({
  enabled: z.boolean(),
  reason: displayText.nullable().optional(),
  value: JsonValueSchema.optional(),
});

/** The three buttons of a Suggested (or conflicting) row: "That's right", "Not quite", "Not us". */
export const FactActionsSchema = z.object({
  confirm: FactActionSchema,
  edit: FactActionSchema,
  not_ours: FactActionSchema,
});

export const BusinessProfileFactSchema = z
  .object({
    ruling_ref: nonBlank,
    kind: nonBlank,
    subtype: nonBlank,
    value: JsonValueSchema,
    /** The value as the page shows it; null exactly when `value` is null. */
    value_display: displayText.nullable(),
    status: FactStatusSchema,
    confirmed_by: ConfirmedBySchema.nullable(),
    /**
     * ERP1-P2-GW: the standing answer's structured value in the fact's one shape (the editor opens
     * with it), on a fact the page writes that names a confirmer, whatever its channel, or that is
     * a page answer held from applying (/ship round 3, C1); null otherwise. Optional: an older
     * route omits it.
     */
    edit_value: z.record(JsonValueSchema).nullable().optional(),
    evidence: z.array(EvidenceRowSchema),
    verify: VerifySchema.nullable(),
    payoff: PayoffSchema.nullable(),
    history: z.array(HistoryEntrySchema),
    /** Why a `held` fact cannot execute (U3 §4): no executor, an open conflict, a missing end date. */
    held_reason: displayText.nullable(),
    /** What failed, for `needs_attention` (XC-BP1 §8 states). */
    attention: displayText.nullable(),
    /**
     * XC-BP1-W2 §4: a Suggested or conflicting row's buttons (ERP1-P2-BP-CONFIRM-ROUTE). Optional:
     * absent on every other fact and from a route before it.
     */
    actions: FactActionsSchema.optional(),
    /**
     * XC-BP1-W2 §4 (ERP1-P2-BP-CONFIRM-ROUTE part 2a): on a one-tap suggestion, what its row says
     * beyond the words its tap files (a count), for the source line. Optional: absent elsewhere.
     */
    source_note: displayText.nullable().optional(),
  })
  .superRefine((f, ctx) => {
    const fail = (message: string, path: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
    const blank = (s: string | null) => s === null || s.trim().length === 0;
    if ((f.value === null) !== (f.value_display === null)) {
      fail('a value is shown by its display text, and display text needs a value', 'value_display');
    }
    if (f.value !== null && f.status !== 'default' && f.evidence.length === 0 && !f.confirmed_by) {
      fail('a displayed value has an evidence row or a confirmer', 'evidence');
    }
    if (f.edit_value != null && !f.confirmed_by && f.status !== 'held') {
      // ERP1-P2-GW: the value the editor opens with is the customer's own standing answer; a page
      // answer an executor holds keeps it (/ship round 3, C1), so a save never drops its names
      fail(
        "an edit value is the customer's own standing answer: it has a confirmer, or it is a page answer held from applying",
        'edit_value',
      );
    }
    if (f.source_note != null && f.status !== 'suggested') {
      fail('only a suggestion carries a source note', 'source_note');
    }
    if (f.actions && f.status !== 'suggested' && f.status !== 'conflict') {
      fail('only a suggested or conflicting fact carries buttons', 'actions');
    }
    if (f.verify?.passed === false && f.status !== 'needs_attention') {
      fail('a fact whose verify failed needs attention', 'status');
    }
    if (shownStrings(f).some((text) => namesAnId(text, factIds(f)))) {
      fail("display text never names the fact's ruling ref or an evidence row id", 'value_display');
    }
    switch (f.status) {
      case 'confirmed':
        if (!f.confirmed_by) fail('a confirmed fact names its confirmer', 'confirmed_by');
        if (f.value === null) fail('a confirmed fact shows the value confirmed', 'value');
        break;
      case 'suggested':
        if (f.evidence.length === 0) fail('a suggested fact cites at least one evidence row', 'evidence');
        if (f.confirmed_by) fail('a suggested fact has no confirmer', 'confirmed_by');
        if (f.value === null) fail('a suggested fact shows the value suggested', 'value');
        break;
      case 'default':
        if (f.confirmed_by) fail('a default has no confirmer', 'confirmed_by');
        break;
      case 'conflict':
        if (f.evidence.length < 2) fail('a conflict cites both sides', 'evidence');
        break;
      case 'held':
        if (blank(f.held_reason)) fail('a held fact names why', 'held_reason');
        break;
      case 'needs_attention':
        if (!f.verify || f.verify.passed) fail('needs attention only after a failed verify', 'verify');
        if (blank(f.attention)) fail('needs attention says what failed', 'attention');
        break;
      case 'unknown':
        if (f.value !== null) fail('an unknown fact has no value', 'value');
        if (f.confirmed_by) fail('an unknown fact has no confirmer', 'confirmed_by');
        // it asserts no answer, so no verify outcome is shown on it (/ship round 9)
        if (f.verify) fail('an unknown fact carries no verify outcome', 'verify');
        break;
    }
  });

export const BusinessProfileGroupSchema = z.object({
  fact_group: FactGroupSchema,
  title: nonBlank,
  facts: z.array(BusinessProfileFactSchema),
});

/**
 * Per-tenant flags (system_config `erp1.in_app_fact_writes_enabled`,
 * `erp1.business_profile_customer_visible`), and how many facts the route
 * withheld: a fact this contract would refuse no longer fails the page; the
 * route sends a `held` fact in its place ("This answer could not be
 * displayed", no value, no evidence) and counts it here (/ship round 10).
 */
export const BusinessProfileMetaSchema = z.object({
  writes_enabled: z.boolean(),
  customer_visible: z.boolean(),
  as_of: isoDateTime,
  facts_withheld: count,
});

/**
 * Earlier answers no fact group takes yet (kind `other`, `price_fact`): shown, never dropped. An
 * unfiled fact answers none of the seven facts, so it is held and never carries buttons (the route's
 * `UnfiledFact`).
 */
export const UnfiledSectionSchema = z.object({
  title: nonBlank,
  facts: z.array(
    BusinessProfileFactSchema.superRefine((f, ctx) => {
      if (f.actions !== undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'an unfiled fact carries no buttons', path: ['actions'] });
      }
      if (f.source_note != null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'an unfiled fact carries no source note', path: ['source_note'] });
      }
    }),
  ),
});

/**
 * ERP1-P2-BP-EDIT (GW /ship round 5, `EditTargetOut`): what the editor of one page-writable fact
 * opens with, decided by the server, which checks every save against the same decision. The page
 * prefills from `value`, sends `based_on` exactly as given, and offers `choices` (the owner's
 * sign-in addresses) as the only values those fields may take. `value` is null when no answer
 * stands, or when the answer it opens from (`opened_from_ref`) is not in the fact's shape: the page
 * then shows that answer's words beside an empty editor. None of it is printed but the words.
 */
export const EditTargetSchema = z.object({
  kind: nonBlank,
  subtype: nonBlank,
  fact_group: FactGroupSchema,
  page_ref: nonBlank,
  opened_from_ref: nonBlank.nullable(),
  based_on: nonBlank.nullable(),
  value: z.record(JsonValueSchema).nullable(),
  choices: z.record(z.array(z.string())).default({}),
});

/**
 * R5 (2026-10-07): one name or domain the engine counts as his company today, and whether a page
 * save may take it off (`removable`: the server applies his strike, `not_ours`, to it). ONE entry
 * per name the engine matches on: `value` is its display spelling, `spellings` (optional) every
 * spelling of it in force; striking the entry strikes `value` and the server removes every spelling.
 * The page shows a value only if it is customer copy (`isDisplayText`), checked where it is drawn, so
 * one odd value locks its field instead of refusing the whole profile.
 */
export const CompanyNameEntrySchema = z.object({
  value: nonBlank,
  removable: z.boolean(),
  spellings: z.array(nonBlank).optional(),
});

/**
 * R5: what the engine counts as his company today, the "Your company" card's starting pills (Legal
 * name, Trade names, Email and web domains). `truncated`: a list was cut short, so a save of it would
 * drop what is not shown; the card then reads its fields only. Optional: a route before R5 omits it,
 * and the card opens from the editor targets and the suggestions instead.
 */
export const CompanyNamesSchema = z.object({
  legal_names: z.array(CompanyNameEntrySchema),
  trade_names: z.array(CompanyNameEntrySchema),
  domains: z.array(CompanyNameEntrySchema),
  truncated: z.boolean().optional(),
});

/**
 * Estevan R7 fix 2: a name on his records that looks like his company's and is not counted yet
 * (`GET /business-profile/company-names/look-alikes`, ER `LookAlikeOut`): `value` is the record's own
 * spelling (what the card's Add puts in Trade names), `looks_like` his name it resembles, `how` the
 * resemblance; `ruled_apart_from`: the records his ruling keeps apart from this one (adding both
 * folds neither); `on_your_books`: a record on his QuickBooks list (the save asks first).
 */
/**
 * Board s1325-1: an earlier answer that two of his names are different companies, which holds a
 * look-alike (ER `EarlierAnswerOut`): its own two spellings, who gave it (`you` / `wingo`) and where,
 * whether he may change it here, and each record it keeps apart with its own documents' titles and
 * whether it is on his QuickBooks lists. No date (Estevan 2026-10-03).
 */
export const EarlierAnswerSchema = z.object({
  key: z.string(),
  entity_type: z.enum(['customer', 'vendor']),
  names: z.tuple([z.string(), z.string()]),
  given_by: z.enum(['you', 'wingo']),
  channel: z.enum(['inbox', 'email', 'page', 'wingo']),
  replaceable: z.boolean().default(false),
  records: z
    .array(
      z.object({
        id: nonBlank,
        name: z.string().nullable().optional(),
        entity_type: z.enum(['customer', 'vendor']),
        on_your_books: z.boolean().default(false),
        documents: z.array(z.object({ title: z.string() })).default([]),
        documents_total: z.number().int().nonnegative().default(0),
      }),
    )
    .default([]),
});

export const LookAlikeNameSchema = z.object({
  value: nonBlank,
  looks_like: z.string().nullable().optional(),
  how: z.enum(['spacing', 'number_added', 'spelling', 'cut_off']),
  records: z.array(z.object({ id: nonBlank, entity_type: z.enum(['customer', 'vendor']) })).min(1),
  ruled_apart_from: z.array(z.string()).default([]),
  on_your_books: z.boolean().default(false),
  earlier_answers: z.array(EarlierAnswerSchema).default([]),
});

/** The look-alike route's payload (ER `CompanyLookAlikesOut`). */
export const CompanyLookAlikesSchema = z.object({
  look_alikes: z.array(LookAlikeNameSchema),
  look_alikes_truncated: z.boolean().optional(),
  look_alikes_unread: z.string().nullable().optional(),
  look_alikes_left: z.record(z.string(), z.number()).optional(),
});

export const BusinessProfileReadModelSchema = z
  .object({
    tenant_id: nonBlank,
    meta: BusinessProfileMetaSchema,
    groups: z.array(BusinessProfileGroupSchema),
    unfiled: UnfiledSectionSchema,
    /** Optional, so a payload from a route before round 5 parses (no editor targets: none known). */
    edit_targets: z.array(EditTargetSchema).optional().default([]),
    /** R5: what the engine counts as his company today (`CompanyNamesSchema`). */
    company_names: CompanyNamesSchema.optional(),
  })
  .superRefine((m, ctx) => {
    const groups = m.groups.map((g) => g.fact_group);
    if (new Set(groups).size !== groups.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a fact group is listed twice', path: ['groups'] });
    }
    const refs = [...m.groups.flatMap((g) => g.facts), ...m.unfiled.facts].map((f) => f.ruling_ref);
    if (new Set(refs).size !== refs.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a ruling ref is listed twice', path: ['groups'] });
    }
  });
